export type PlanType = 'free' | 'team5' | 'team10' | 'business25' | 'enterprise';

export const PLAN_LIMITS: Record<PlanType, number> = {
  free: 1,
  team5: 5,
  team10: 10,
  business25: 25,
  enterprise: 99999,
};

export type TemplateId = 'modern_horizontal' | 'clean_minimal' | 'corporate_compact' | 'marketing_focus';

export interface SignatureFieldToggles {
  showAvatar: boolean;
  avatarShape: 'circle' | 'square' | 'rounded';
  showJobTitle: boolean;
  showDepartment: boolean;
  showCompany: boolean;
  showPhone: boolean;
  showEmail: boolean;
  showWebsite: boolean;
  showTaxId: boolean;
  showAddress: boolean;
  showSocials: boolean;
  showBanner: boolean;
  showDisclaimer: boolean;
  showFreemiumBranding: boolean;
}

export interface SocialLinks {
  linkedin?: string;
  facebook?: string;
  instagram?: string;
  twitter?: string;
  github?: string;
  youtube?: string;
}

export interface SocialItem {
  platform: string;
  url: string;
  enabled: boolean;
}

export interface SavedSignatureTemplate {
  id: string;
  name: string;
  templateId: TemplateId;
  primaryColor: string;
  accentColor?: string;
  toggles: SignatureFieldToggles;
  socials?: SocialItem[] | SocialLinks;
  companyName?: string;
  companyPhone?: string;
  companyMobile?: string;
  defaultAvatarPlaceholder?: string;
  websiteUrl?: string;
  nip?: string;
  regon?: string;
  address?: string;
  logoUrl?: string;
  logoWidth?: number;
  logoHeight?: number;
  bannerUrl?: string;
  bannerLink?: string;
  bannerAlt?: string;
  disclaimer?: string;
  promoTitle?: string;
  promoText?: string;
  promoCtaText?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface UserGroup {
  id: string;
  name: string;
  description?: string;
  color: string;
  savedTemplateId?: string;
  createdAt: string;
}

export interface WorkspaceUser {
  id: string;
  primaryEmail: string;
  fullName: string;
  givenName?: string;
  familyName?: string;
  jobTitle?: string;
  department?: string;
  phone?: string;
  mobile?: string;
  showMobile?: boolean;
  useCustomPhone?: boolean;
  emailAlias?: string;
  useCustomWebsite?: boolean;
  customWebsiteUrl?: string;
  useCustomAddress?: boolean;
  customAddress?: string;
  avatarUrl?: string;
  nip?: string;
  signatureStatus?: 'synced' | 'pending' | 'error' | 'not_configured';
  lastSyncedAt?: string;
  groupId?: string;
  customTemplateId?: string;
  assignedSignatureId?: string;
}

export interface OrganizationConfig {
  domain: string;
  companyName: string;
  nip: string;
  logoUrl: string;
  logoWidth?: number;
  logoHeight?: number;
  websiteUrl: string;
  bannerUrl?: string;
  bannerLink?: string;
  bannerAlt?: string;
  plan?: PlanType;
}

export interface SignatureEmployeeData {
  fullName: string;
  jobTitle?: string;
  department?: string;
  phone?: string;
  mobile?: string;
  showMobile?: boolean;
  useCustomPhone?: boolean;
  email: string;
  emailAlias?: string;
  avatarUrl?: string;
  defaultAvatarPlaceholder?: string;
  nip?: string;
  regon?: string;
  companyName?: string;
  companyPhone?: string;
  address?: string;
  useCustomAddress?: boolean;
  customAddress?: string;
  logoUrl?: string;
  logoWidth?: number;
  logoHeight?: number;
  websiteUrl?: string;
  useCustomWebsite?: boolean;
  customWebsiteUrl?: string;
  bannerUrl?: string;
  bannerLink?: string;
  bannerAlt?: string;
  promoTitle?: string;
  promoText?: string;
  promoCtaText?: string;
  disclaimer?: string;
  templateId?: TemplateId;
  primaryColor?: string;
  accentColor?: string;
  toggles?: SignatureFieldToggles;
  socials?: SocialItem[] | SocialLinks;
  plan?: PlanType;
}

export interface BatchUpdateItemResult {
  email: string;
  fullName: string;
  success: boolean;
  error?: string;
  updatedAt: string;
}

export interface BatchUpdateResponse {
  batchId: string;
  total: number;
  successful: number;
  failed: number;
  results: BatchUpdateItemResult[];
  completedAt: string;
}

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

